import React from 'react'

/**
 * Props pour le composant ErrorBoundary
 */
interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/**
 * État du composant ErrorBoundary
 */
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Composant qui capture les erreurs dans ses enfants et affiche une interface de secours
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  /**
   * Met à jour l'état quand une erreur est capturée
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Met à jour l'état pour afficher l'UI de secours
    return { hasError: true, error }
  }

  /**
   * Traite l'erreur capturée (logging, reporting, etc.)
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Vous pouvez également enregistrer l'erreur dans un service de reporting
    console.error('ErrorBoundary a capturé une erreur', error, errorInfo)

    // Appeler le callback onError si fourni
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  /**
   * Affiche l'interface de secours ou les enfants
   */
  render(): React.ReactNode {
    if (this.state.hasError) {
      // Afficher l'interface de secours personnalisée si fournie
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Interface de secours par défaut
      return (
        <div className="text-center p-4">
          <h1 className="text-xl font-bold text-red-500">
            Une erreur est survenue.
          </h1>
          <p className="text-gray-500">
            Veuillez actualiser la page ou réessayer plus tard.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
